// The app's side of connections: the remote MCP servers someone signs in to
// once. Signing in happens in a small window the provider controls; the board
// hears it finished from that window, or from the server over the socket,
// whichever comes first.

import type { Connection } from "../../shared/types";
import { api } from "../lib/api";
import { runPull } from "./sources";
import { get, set } from "./store";

function put(c: Connection) {
  set((s) => ({ connections: { ...s.connections, [c.id]: c } }));
}

export interface ConnectResult {
  connection: Connection;
  /** set when the server needs a sign-in; signIn() opens it */
  authorizeUrl: string | null;
}

/** Open the sign-in window now, on the click, so no popup blocker stands in the way; point it later. */
export function openSignInWindow(): Window | null {
  const win = window.open("", "cb-signin", popupFeatures());
  if (win) {
    try {
      win.document.title = "Signing in…";
      win.document.body.style.cssText = "margin:0;height:100vh;display:grid;place-items:center;background:#0b1530;color:#a9b6d8;font:15px system-ui,sans-serif";
      win.document.body.textContent = "Opening the sign-in page…";
    } catch {
      /* not ours to touch yet */
    }
  }
  return win;
}

/** What the page shows while someone signs in: the window, whether a blocker stopped it, and the ways on. */
export interface SignInWait {
  /** the sign-in page's address */
  url: string;
  /** the browser blocked the window: reopen() opens it, on a click */
  blocked: boolean;
  /** open the sign-in window again (or for the first time, after a blocker): true when it opened */
  reopen: () => boolean;
  /** stop waiting; the promise rejects with SIGN_IN_CANCELLED */
  cancel: () => void;
}

export interface SignInOpts {
  /** a server known to need no sign-in should not flash a window */
  signIn?: boolean;
  /** told when the sign-in page is open (or blocked), so the page can say what is happening and offer a way on */
  onWait?: (w: SignInWait) => void;
  /** told when the window closes before the board hears back; waiting goes on, since a sign-in can still land */
  onClosed?: () => void;
}

export const SIGN_IN_CANCELLED = "Signing in was cancelled.";

/**
 * Wait until the connection signs in: the window says so, or the server does. With onClosed, a closed window is
 * only news (some sign-in pages cut the link to the window that opened them, so it looks closed while open);
 * without it, a window closed for good ends the wait as before.
 */
function waitForSignIn(id: string, url: string, first: Window | null, opts: SignInOpts): Promise<Connection> {
  return new Promise((resolve, reject) => {
    let done = false;
    let win = first;
    let told = false;
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      channel?.close();
      clearInterval(timer);
      fn();
    };
    const check = () => {
      const c = get().connections[id];
      if (c?.status === "ok") finish(() => resolve(c));
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== location.origin || (e.data as { type?: string })?.type !== "cb:mcp-auth") return;
      const d = e.data as { ok: boolean; id?: string };
      if (d.id && d.id !== id) return;
      if (d.ok) void refresh(id).then((c) => finish(() => resolve(c)), () => check());
      else void refresh(id).catch(() => null).then(() => finish(() => reject(new Error(get().connections[id]?.error ?? "Signing in did not finish."))));
    };
    window.addEventListener("message", onMessage);
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("cb-mcp-auth");
      channel.onmessage = (e) => onMessage(new MessageEvent("message", { data: e.data, origin: location.origin }));
    } catch {
      channel = null;
    }
    let started = Date.now();
    opts.onWait?.({
      url,
      blocked: !first,
      reopen: () => {
        if (done) return false;
        if (win && !win.closed) {
          win.focus();
          return true;
        }
        win = window.open(url, "cb-signin", popupFeatures());
        started = Date.now();
        told = false;
        return !!win;
      },
      cancel: () => {
        try {
          win?.close();
        } catch {
          /* gone */
        }
        finish(() => reject(new Error(SIGN_IN_CANCELLED)));
      },
    });
    const timer = setInterval(() => {
      check();
      // the window was closed without finishing
      if (win && win.closed && !done && !told && Date.now() - started > 1500) {
        told = true;
        setTimeout(() => {
          check();
          if (done) return;
          if (opts.onClosed) opts.onClosed();
          else finish(() => reject(new Error("The sign-in window was closed before it finished.")));
        }, 1200);
      }
      if (Date.now() - started > 15 * 60_000) finish(() => reject(new Error("Signing in took too long. Try again.")));
    }, 700);
  });
}

function popupFeatures(): string {
  const w = 520;
  const h = 720;
  const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
  return `popup=yes,width=${w},height=${h},left=${left},top=${top}`;
}

async function refresh(id: string): Promise<Connection> {
  const s = await api<{ connections: Connection[] }>("/api/snapshot");
  const c = s.connections.find((x) => x.id === id);
  if (!c) throw new Error("That connection is gone.");
  put(c);
  return c;
}

/** Point the window opened on the click at the sign-in page, or say it was blocked, then wait. */
function signInAt(c: Connection, url: string, win: Window | null, opts: SignInOpts): Promise<Connection> {
  if (!win && !opts.onWait) throw new Error("Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.");
  if (win) win.location.href = url;
  return waitForSignIn(c.id, url, win, opts);
}

/**
 * Connect by address. Opens a sign-in window when the server wants one, and
 * resolves once it is connected (or needs a key instead).
 */
export async function connectServer(url: string, name?: string, opts: SignInOpts = {}): Promise<Connection> {
  const win = opts.signIn === false ? null : openSignInWindow();
  try {
    const r = await api<ConnectResult>("/api/connections", { body: { url, name } });
    put(r.connection);
    if (!r.authorizeUrl) {
      win?.close();
      return r.connection;
    }
    return await signInAt(r.connection, r.authorizeUrl, win ?? (opts.signIn === false ? openSignInWindow() : null), opts);
  } catch (e) {
    win?.close();
    throw e;
  }
}

export async function signInAgain(id: string, opts: SignInOpts = {}): Promise<Connection> {
  const win = openSignInWindow();
  try {
    const r = await api<ConnectResult>(`/api/connections/${id}/signin`, { body: {} });
    put(r.connection);
    if (!r.authorizeUrl) {
      win?.close();
      return r.connection;
    }
    const c = await signInAt(r.connection, r.authorizeUrl, win, opts);
    if (c.status === "ok") readAgain(id);
    return c;
  } catch (e) {
    win?.close();
    throw e;
  }
}

/** Signed in again: the sources that read through the connection run now, so their widgets fill at once, not at their next turn. */
function readAgain(id: string) {
  for (const p of Object.values(get().pulls)) if (p.config.connection === id) void runPull(p.id).catch(() => {});
}

export async function saveKey(id: string, value: string, header?: string): Promise<Connection> {
  const r = await api<{ connection: Connection }>(`/api/connections/${id}/key`, { body: { value, header } });
  put(r.connection);
  return r.connection;
}

export async function runTool(id: string, tool: string, args: Record<string, unknown>): Promise<unknown> {
  const r = await api<{ sample: unknown }>(`/api/connections/${id}/call`, { body: { tool, args } });
  return r.sample;
}

export async function removeConnection(id: string): Promise<void> {
  await api(`/api/connections/${id}`, { method: "DELETE" });
  set((s) => {
    const connections = { ...s.connections };
    delete connections[id];
    return { connections };
  });
}
